import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Switch,
} from "@mui/material";
import AdminNavbar from "./AdminNavbar";
import PlacementTestAnalytics from "./PlacementTestAnalytics";
import CourseInterestEmailGenerator from "./CourseInterestEmailGenerator";
import api from "lib/api/api";

const CURRENCIES = [
  { key: "egp", label: "EGP" },
  { key: "eur", label: "EUR" },
  { key: "aed", label: "AED" },
];
const SOCIAL_PLATFORMS = ["facebook", "instagram", "whatsapp", "linkedin", "youtube", "tiktok", "telegram"];
const MANAGER_PERMISSION_LABELS = {
  viewCourseModeration: "View pending and approved courses",
  moderateCourses: "Approve courses and return approved courses to pending",
  archiveCourses: "Archive teacher courses",
  deleteCourses: "Permanently delete eligible teacher courses",
  viewCourseInterests: "View interested students",
  updateCourseInterests: "Update lead status and internal notes",
  deleteCourseInterests: "Delete course-interest records",
  manageInterestOfferings: "Create, activate, pause and archive interest-only offerings",
  viewPlacementAnalytics: "View placement-test analytics",
  viewPlacementDetailedReports: "Preview and download detailed placement reports",
  sendPlacementResultEmails: "Send the standard placement-result email",
  sendPlacementDetailedReports: "Send detailed placement reports as PDF attachments",
};

const RECOVERY_CATEGORIES = {
  COMPLETED: { label: "Completed", color: "success", meaning: "A saved answer and final evaluation exist." },
  IN_PROGRESS: { label: "In progress", color: "info", meaning: "An active, recent session has no submitted answer yet." },
  TIME_EXPIRED_RETAKE_REQUIRED: { label: "Time expired / retake", color: "warning", meaning: "The 75-minute test window plus submission grace has expired without a saved submission. Release it for a clean retake." },
  ABANDONED: { label: "Abandoned", color: "default", meaning: "No answer was submitted and the session is older than the recovery threshold." },
  READY_FOR_RE_EVALUATION: { label: "Ready for re-evaluation", color: "warning", meaning: "A fully scored answer exists, but its evaluation is missing." },
  SUBMISSION_FAILED_RETAKE_REQUIRED: { label: "Submission failed / retake required", color: "error", meaning: "A recorded processing error exists and no recoverable answer was saved." },
  EMAIL_FAILED: { label: "Email failed", color: "error", meaning: "The evaluation exists, but the latest result-email attempt failed." },
  EVALUATION_CONFLICT: { label: "Evaluation conflict", color: "error", meaning: "This answer is scored, but the student already has an evaluation linked to another session. Review before recovery." },
  EXISTING_RESULT_UNFINISHED_SESSION: { label: "Unfinished duplicate", color: "warning", meaning: "This unfinished session has no submitted answer, while the student already has a valid result from another session." },
  RETAKE_RESOLVED: { label: "Retake released", color: "success", meaning: "The unfinished attempt was released and no longer counts against the student's attempt limit." },
  SUPERSEDED: { label: "Superseded", color: "default", meaning: "An older/different session was closed while the student's existing evaluation was preserved." },
};

const DEFAULT_APOLOGY_EMAIL = {
  subject: "Please retake your ALGermanist placement test",
  messageEn: "Hello {studentName},\n\nWe identified a technical problem while processing your placement test. The problem has now been corrected.\n\nYour previous failed attempt has been released, so you can start the placement test again. We apologize for the inconvenience.\n\nALGermanist Team",
  messageDe: "Hallo {studentName},\n\nwir haben ein technisches Problem bei der Verarbeitung deines Einstufungstests festgestellt. Das Problem wurde inzwischen behoben.\n\nDein vorheriger fehlgeschlagener Versuch wurde freigegeben, sodass du den Einstufungstest erneut starten kannst. Wir entschuldigen uns für die Unannehmlichkeiten.\n\nALGermanist Team",
  messageAr: "مرحبًا {studentName}،\n\nلقد اكتشفنا مشكلة تقنية أثناء معالجة اختبار تحديد المستوى الخاص بك، وقد تم حل المشكلة الآن.\n\nتم إلغاء احتساب المحاولة السابقة التي لم تكتمل، ويمكنك الآن بدء اختبار تحديد المستوى مرة أخرى. نعتذر لك عن الإزعاج.\n\nفريق ALGermanist",
};

const DEFAULT_MAINTENANCE_EMAIL = {
  subject: "ALGermanist scheduled maintenance and new features",
  messageEn: "We are optimizing the website and releasing new features. Please do not worry if the website is temporarily unavailable or refresh it repeatedly. The maintenance should take approximately 30 minutes. Thank you for your patience.",
  messageDe: "Wir optimieren die Website und veröffentlichen neue Funktionen. Bitte machen Sie sich keine Sorgen, falls die Website vorübergehend nicht erreichbar ist, und laden Sie die Seite nicht wiederholt neu. Die Wartung dauert voraussichtlich etwa 30 Minuten. Vielen Dank für Ihre Geduld.",
  messageAr: "نقوم حاليًا بتحسين الموقع وإضافة خصائص جديدة. يُرجى عدم القلق إذا كان الموقع غير متاح مؤقتًا، وعدم إعادة تحميل الصفحة بشكل متكرر. ستستغرق أعمال الصيانة نحو 30 دقيقة. شكرًا لصبركم.",
};

const DEFAULT_FEATURE_RELEASE_EMAIL = {
  subject: "New at ALGermanist",
  messageEn: "We have released a new feature to make your ALGermanist experience even better. Sign in to discover what is new.",
  messageDe: "Wir haben eine neue Funktion veröffentlicht, die deine Erfahrung mit ALGermanist noch besser macht. Melde dich an und entdecke die Neuigkeiten.",
  messageAr: "أطلقنا ميزة جديدة لجعل تجربتك مع ALGermanist أفضل. سجّل الدخول واكتشف الجديد.",
  linkUrl: "https://algermanist.com",
  linkTextEn: "Discover the new feature",
  linkTextDe: "Neue Funktion entdecken",
  linkTextAr: "اكتشف الميزة الجديدة",
  imagePosition: "TOP",
};

const AdminSettingsPage = () => {
  const [tab, setTab] = useState(0);
  const [courses, setCourses] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [savingAttempts, setSavingAttempts] = useState(false);
  const [placementSessions, setPlacementSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionAction, setSessionAction] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [placementFilters, setPlacementFilters] = useState({ search: "", category: "", accountType: "", emailStatus: "", targetLevel: "" });
  const [placementPage, setPlacementPage] = useState(0);
  const [placementTotalPages, setPlacementTotalPages] = useState(0);
  const [placementTotalElements, setPlacementTotalElements] = useState(0);
  const [technicalDetails, setTechnicalDetails] = useState(null);
  const [apologyPreviewOpen, setApologyPreviewOpen] = useState(false);
  const [apologyEmail, setApologyEmail] = useState(DEFAULT_APOLOGY_EMAIL);
  const [placementGuests, setPlacementGuests] = useState([]);
  const [guestEmailDrafts, setGuestEmailDrafts] = useState({});
  const [guestSearch, setGuestSearch] = useState("");
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [guestAction, setGuestAction] = useState(null);
  const [socialLinks, setSocialLinks] = useState(() => SOCIAL_PLATFORMS.reduce((result, platform) => ({ ...result, [platform]: "" }), {}));
  const [loadingSocialLinks, setLoadingSocialLinks] = useState(false);
  const [savingSocialLinks, setSavingSocialLinks] = useState(false);
  const [managerPermissions, setManagerPermissions] = useState({});
  const [loadingManagerPermissions, setLoadingManagerPermissions] = useState(false);
  const [savingManagerPermissions, setSavingManagerPermissions] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ generalPaymentsEnabled: true, onlinePaymentsEnabled: true, egyptianBankTransferEnabled: true, instaPayEnabled: true, egyptianBankIban: "" });
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  const [maintenanceEmail, setMaintenanceEmail] = useState(DEFAULT_MAINTENANCE_EMAIL);
  const [maintenancePreviewOpen, setMaintenancePreviewOpen] = useState(false);
  const [sendingMaintenance, setSendingMaintenance] = useState(false);
  const [featureReleaseEmail, setFeatureReleaseEmail] = useState(DEFAULT_FEATURE_RELEASE_EMAIL);
  const [featureReleaseImage, setFeatureReleaseImage] = useState(null);
  const [featureReleaseImagePreview, setFeatureReleaseImagePreview] = useState("");
  const [featureReleasePreviewOpen, setFeatureReleasePreviewOpen] = useState(false);
  const [sendingFeatureRelease, setSendingFeatureRelease] = useState(false);

  const loadPrices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/courses/Admin/prices");
      const rows = response.data || [];
      setCourses(rows);
      setDrafts(
        rows.reduce((acc, course) => {
          acc[course.courseId] = {
            egp: course.egp ?? "",
            eur: course.eur ?? "",
            aed: course.aed ?? "",
          };
          return acc;
        }, {})
      );
    } catch (err) {
      setError("Could not load course prices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  const loadPlacementSettings = useCallback(async () => {
    setLoadingAttempts(true);
    setError("");
    try {
      const response = await api.get("/admin/settings/placement-test");
      setMaxAttempts(response.data?.maxAttempts ?? 3);
    } catch (err) {
      setError("Could not load placement test settings.");
    } finally {
      setLoadingAttempts(false);
    }
  }, []);

  useEffect(() => {
    loadPlacementSettings();
  }, [loadPlacementSettings]);

  const loadPlacementSessions = useCallback(async (page = placementPage, filters = placementFilters) => {
    setLoadingSessions(true);
    try {
      const response = await api.get("/admin/settings/placement-test/sessions", {
        params: { page, size: 25, ...filters },
      });
      setPlacementSessions(response.data?.content || []);
      setPlacementTotalPages(response.data?.totalPages || 0);
      setPlacementTotalElements(response.data?.totalElements || 0);
      setPlacementPage(page);
      setSelectedSessions([]);
    } catch (err) {
      setError("Could not load placement test sessions.");
    } finally {
      setLoadingSessions(false);
    }
  }, [placementFilters, placementPage]);

  useEffect(() => {
    loadPlacementSessions(0);
    // Filters are intentionally applied only when the admin presses Search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlacementGuests = useCallback(async (search = "") => {
    setLoadingGuests(true);
    try {
      const response = await api.get("/admin/settings/placement-test/guests", {
        params: { search: search.trim(), page: 0, size: 100 },
      });
      const rows = response.data?.content || [];
      setPlacementGuests(rows);
      setGuestEmailDrafts(
        rows.reduce((draftsById, guest) => {
          draftsById[guest.userId] = guest.email || "";
          return draftsById;
        }, {})
      );
    } catch (err) {
      setError(err.response?.data?.message || "Could not load placement guest accounts.");
    } finally {
      setLoadingGuests(false);
    }
  }, []);

  useEffect(() => {
    loadPlacementGuests();
  }, [loadPlacementGuests]);

  const loadSocialLinks = useCallback(async () => {
    setLoadingSocialLinks(true);
    try {
      const response = await api.get("/admin/settings/social-media");
      setSocialLinks(SOCIAL_PLATFORMS.reduce((result, platform) => ({ ...result, [platform]: response.data?.[platform] || "" }), {}));
    } catch (err) {
      setError(err.response?.data?.message || "Could not load social media links.");
    } finally {
      setLoadingSocialLinks(false);
    }
  }, []);

  useEffect(() => { loadSocialLinks(); }, [loadSocialLinks]);

  const loadManagerPermissions = useCallback(async () => {
    setLoadingManagerPermissions(true);
    try { const response = await api.get("/admin/settings/teacher-manager-permissions"); setManagerPermissions(response.data || {}); }
    catch (err) { setError(err.response?.data?.message || "Could not load Teacher Manager permissions."); }
    finally { setLoadingManagerPermissions(false); }
  }, []);
  useEffect(() => { loadManagerPermissions(); }, [loadManagerPermissions]);

  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try { const response = await api.get("/admin/settings/payment-methods"); setPaymentMethods(response.data); }
    catch (err) { setError(err.response?.data?.message || "Could not load payment method settings."); }
    finally { setLoadingPaymentMethods(false); }
  }, []);
  useEffect(() => { loadPaymentMethods(); }, [loadPaymentMethods]);

  const savePaymentMethods = async () => {
    setSavingPaymentMethods(true); setError(""); setSuccess("");
    const normalizedIban = String(paymentMethods.egyptianBankIban || "").replace(/\s+/g, "").toUpperCase();
    if (paymentMethods.egyptianBankTransferEnabled && !/^EG[0-9A-Z]{20,32}$/.test(normalizedIban)) {
      setError("Enter a valid Egyptian IBAN before enabling Egyptian bank transfer.");
      setSavingPaymentMethods(false);
      return;
    }
    try { const response = await api.put("/admin/settings/payment-methods", paymentMethods); setPaymentMethods(response.data); setSuccess("Payment method visibility saved."); }
    catch (err) { setError(err.response?.data?.message || "Could not save payment method settings."); }
    finally { setSavingPaymentMethods(false); }
  };

  const sendMaintenanceEmail = async () => {
    setSendingMaintenance(true); setError(""); setSuccess("");
    try {
      const response = await api.post("/admin/settings/maintenance-email", maintenanceEmail);
      const result = response.data || {};
      const summary = `Maintenance email sent to ${result.sent || 0} of ${result.recipients || 0} users${result.failed ? `; ${result.failed} failed or were not attempted` : ""}.`;
      if (result.failed) setError(summary); else { setSuccess(summary); setMaintenancePreviewOpen(false); }
    } catch (err) { setError(err.response?.data?.message || "Could not send the maintenance email."); }
    finally { setSendingMaintenance(false); }
  };

  const selectFeatureReleaseImage = (event) => {
    const file = event.target.files?.[0] || null;
    if (featureReleaseImagePreview) URL.revokeObjectURL(featureReleaseImagePreview);
    setFeatureReleaseImage(file);
    setFeatureReleaseImagePreview(file ? URL.createObjectURL(file) : "");
    event.target.value = "";
  };

  const sendFeatureReleaseEmail = async () => {
    setSendingFeatureRelease(true); setError(""); setSuccess("");
    try {
      const body = new FormData();
      body.append("data", new Blob([JSON.stringify(featureReleaseEmail)], { type: "application/json" }));
      if (featureReleaseImage) body.append("image", featureReleaseImage);
      const response = await api.post("/admin/settings/feature-release-email", body, { headers: { "Content-Type": "multipart/form-data" } });
      const result = response.data || {};
      const summary = `Feature release email sent to ${result.sent || 0} of ${result.recipients || 0} users${result.failed ? `; ${result.failed} failed or were not attempted` : ""}.`;
      if (result.failed) setError(summary); else { setSuccess(summary); setFeatureReleasePreviewOpen(false); }
    } catch (err) { setError(err.response?.data?.message || "Could not send the feature release email."); }
    finally { setSendingFeatureRelease(false); }
  };

  const saveManagerPermissions = async () => {
    setSavingManagerPermissions(true); setError(""); setSuccess("");
    try { const response = await api.put("/admin/settings/teacher-manager-permissions", managerPermissions); setManagerPermissions(response.data); setSuccess("Teacher Manager permissions saved."); }
    catch (err) { setError(err.response?.data?.message || "Could not save Teacher Manager permissions."); }
    finally { setSavingManagerPermissions(false); }
  };

  const saveSocialLinks = async () => {
    setSavingSocialLinks(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.put("/admin/settings/social-media", socialLinks);
      setSocialLinks(response.data);
      setSuccess("Social media links saved.");
    } catch (err) {
      setError(err.response?.data?.message || "Could not save social media links.");
    } finally {
      setSavingSocialLinks(false);
    }
  };

  const updateGuestEmail = async (guest) => {
    const email = (guestEmailDrafts[guest.userId] || "").trim();
    if (!email) {
      setError("Enter an email address.");
      return;
    }
    setGuestAction(`email-${guest.userId}`);
    setError("");
    setSuccess("");
    try {
      await api.put(`/admin/settings/placement-test/guests/${guest.userId}/email`, { email });
      setSuccess("Guest email updated. Previous verification links were invalidated.");
      await loadPlacementGuests(guestSearch);
    } catch (err) {
      setError(err.response?.data?.message || "Could not update the guest email.");
    } finally {
      setGuestAction(null);
    }
  };

  const sendGuestTestEmail = async (guest) => {
    setGuestAction(`test-${guest.userId}`);
    setError("");
    setSuccess("");
    try {
      await api.post(`/admin/settings/placement-test/guests/${guest.userId}/test-email`);
      setSuccess(`Test email sent to ${guest.email}.`);
    } catch (err) {
      setError(err.response?.data?.message || "Could not send the guest test email.");
    } finally {
      setGuestAction(null);
    }
  };

  const deletePlacementGuest = async (guest) => {
    const label = [guest.firstName, guest.lastName].filter(Boolean).join(" ") || guest.email;
    const confirmed = window.confirm(
      `Permanently delete the placement guest account for ${label}?\n\nAll placement sessions, answers, evaluations, attempts and pending verification links for this guest will also be deleted. This cannot be undone.`
    );
    if (!confirmed) return;
    setGuestAction(`delete-${guest.userId}`);
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/settings/placement-test/guests/${guest.userId}`);
      setSuccess("Placement guest account and all related data deleted.");
      await loadPlacementGuests(guestSearch);
      await loadPlacementSessions();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete the placement guest account.");
    } finally {
      setGuestAction(null);
    }
  };

  const closePlacementSession = async (sessionId) => {
    setSessionAction(`close-${sessionId}`);
    setError("");
    setSuccess("");
    try {
      await api.post(`/admin/settings/placement-test/sessions/${sessionId}/close`);
      setSuccess("Placement test session closed.");
      await loadPlacementSessions();
    } catch (err) {
      setError(err.response?.data?.message || "Could not close the placement test session.");
    } finally {
      setSessionAction(null);
    }
  };

  const resetPlacementAttempts = async (row) => {
    if (!row.targetLevel) return;
    const confirmed = window.confirm(
      `Reset all ${row.targetLevel} placement attempts for ${row.studentName || row.email || `student ${row.studentId}`}?`
    );
    if (!confirmed) return;
    setSessionAction(`reset-${row.studentId}-${row.targetLevel}`);
    setError("");
    setSuccess("");
    try {
      await api.delete(
        `/admin/settings/placement-test/students/${row.studentId}/attempts/${row.targetLevel}`
      );
      setSuccess(`${row.targetLevel} placement attempts reset.`);
      await loadPlacementSessions();
    } catch (err) {
      setError(err.response?.data?.message || "Could not reset placement test attempts.");
    } finally {
      setSessionAction(null);
    }
  };

  const formatSessionDate = (value) =>
    value ? new Date(value).toLocaleString() : "-";

  const selectedRows = placementSessions.filter((row) => selectedSessions.includes(row.sessionId));
  const toggleSession = (sessionId) => setSelectedSessions((current) =>
    current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId]
  );

  const runRecoveryAction = async (action, rows = selectedRows, payload = undefined) => {
    if (!rows.length) return;
    setSessionAction(action);
    setError("");
    setSuccess("");
    let completed = 0;
    const failures = [];
    for (const row of rows) {
      try {
        const response = await api.post(`/admin/settings/placement-test/sessions/${row.sessionId}/${action}`, payload);
        completed += 1;
        if (action === "allow-retake-and-notify" && response.data?.emailSent === false) {
          failures.push(`${row.studentName || row.email || row.sessionId}: retake allowed, but notification email failed`);
        }
      } catch (err) {
        failures.push(`${row.studentName || row.email || row.sessionId}: ${err.response?.data?.message || "failed"}`);
      }
    }
    setSessionAction(null);
    if (completed) setSuccess(`${completed} placement recovery action${completed === 1 ? "" : "s"} completed.`);
    if (failures.length) setError(failures.join(" | "));
    await loadPlacementSessions();
  };

  const viewTechnicalDetails = async (row) => {
    setSessionAction(`details-${row.sessionId}`);
    try {
      const response = await api.get(`/admin/settings/placement-test/sessions/${row.sessionId}/history`);
      setTechnicalDetails({ row, history: response.data || [] });
    } catch (err) {
      setError(err.response?.data?.message || "Could not load recovery history.");
    } finally {
      setSessionAction(null);
    }
  };

  const savePlacementSettings = async () => {
    const value = Number(maxAttempts);
    if (!Number.isInteger(value) || value < 1 || value > 100) {
      setError("Maximum attempts must be a whole number between 1 and 100.");
      return;
    }
    setSavingAttempts(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.put("/admin/settings/placement-test", { maxAttempts: value });
      setMaxAttempts(response.data.maxAttempts);
      setSuccess("Placement test attempt limit saved.");
    } catch (err) {
      setError(err.response?.data?.message || "Could not save placement test settings.");
    } finally {
      setSavingAttempts(false);
    }
  };

  const handleDraftChange = (courseId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [courseId]: {
        ...(prev[courseId] || {}),
        [key]: value,
      },
    }));
  };

  const saveCoursePrices = async (courseId) => {
    const draft = drafts[courseId] || {};
    const payload = CURRENCIES.reduce((acc, currency) => {
      const value = draft[currency.key];
      acc[currency.key] = value === "" ? null : Number(value);
      return acc;
    }, {});

    setSavingId(courseId);
    setError("");
    setSuccess("");
    try {
      const response = await api.put(`/courses/Admin/prices/${courseId}`, payload);
      const updated = response.data;
      setCourses((prev) =>
        prev.map((course) => (course.courseId === courseId ? updated : course))
      );
      setDrafts((prev) => ({
        ...prev,
        [courseId]: {
          egp: updated.egp ?? "",
          eur: updated.eur ?? "",
          aed: updated.aed ?? "",
        },
      }));
      setSuccess("Course prices saved.");
    } catch (err) {
      setError("Could not save course prices.");
    } finally {
      setSavingId(null);
    }
  };

  const courseLabel = (course) =>
    course.parentTitle ? `${course.parentTitle} / ${course.title}` : course.title;

  const sendTestEmail = async (mailbox) => {
    setError("");
    setSuccess("");
    if (!testEmail.trim()) {
      setError("Enter an email address for the test.");
      return;
    }
    setSendingTest(mailbox);
    try {
      const response = await api.post("/admin/settings/test-email", {
        recipient: testEmail.trim(),
        mailbox,
      });
      const modeText = response.data?.mode == null ? "" : ` Active mode: ${response.data.mode}.`;
      setSuccess(`${response.data?.message || "Test email sent successfully."} Sender: ${response.data?.senderAddress}.${modeText}`);
    } catch (err) {
      setError(err.response?.data?.message || "Could not send the test email.");
    } finally {
      setSendingTest(null);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f7fb" }}>
      <AdminNavbar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Settings
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(event, value) => setTab(value)}>
            <Tab label="Course Prices" />
            <Tab label="Email Test" />
            <Tab label="Placement Test" />
            <Tab label="Social Media" />
            <Tab label="Teacher Manager" />
            <Tab label="Payment Methods" />
            <Tab label="Placement Analytics" />
            <Tab label="Email Generation" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Course prices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit each course price in EGP, EUR, and AED.
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              {loading ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Course</TableCell>
                        <TableCell>Level</TableCell>
                        {CURRENCIES.map((currency) => (
                          <TableCell key={currency.key}>{currency.label}</TableCell>
                        ))}
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {courses.map((course) => (
                        <TableRow key={course.courseId}>
                          <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>
                            {courseLabel(course)}
                          </TableCell>
                          <TableCell>{course.level || "-"}</TableCell>
                          {CURRENCIES.map((currency) => (
                            <TableCell key={currency.key}>
                              <TextField
                                type="number"
                                size="small"
                                value={drafts[course.courseId]?.[currency.key] ?? ""}
                                onChange={(event) =>
                                  handleDraftChange(
                                    course.courseId,
                                    currency.key,
                                    event.target.value
                                  )
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                          ))}
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              onClick={() => saveCoursePrices(course.courseId)}
                              disabled={savingId === course.courseId}
                            >
                              {savingId === course.courseId ? "Saving..." : "Save"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Paper>
        )}

        {tab === 1 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2} sx={{ maxWidth: 560 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Test email configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send a real test message from either configured mailbox to the recipient below.
                </Typography>
              </Box>
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}
              <TextField
                label="Recipient email"
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                disabled={Boolean(sendingTest)}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  onClick={() => sendTestEmail("GENERAL")}
                  disabled={Boolean(sendingTest) || !testEmail.trim()}
                >
                  {sendingTest === "GENERAL" ? "Sending..." : "Test info@algermanist.com"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => sendTestEmail("RECRUITMENT")}
                  disabled={Boolean(sendingTest) || !testEmail.trim()}
                >
                  {sendingTest === "RECRUITMENT" ? "Sending..." : "Test theresa.dietrich@algermanist.com"}
                </Button>
              </Stack>
              <Box sx={{ pt: 2, mt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" fontWeight={700}>Maintenance announcement</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>Edit all three languages and preview the email before sending it to every user.</Typography>
                <Stack spacing={2}>
                  <TextField label="Email subject" value={maintenanceEmail.subject} onChange={(event) => setMaintenanceEmail((current) => ({ ...current, subject: event.target.value }))} fullWidth />
                  <TextField label="English message" value={maintenanceEmail.messageEn} onChange={(event) => setMaintenanceEmail((current) => ({ ...current, messageEn: event.target.value }))} multiline minRows={4} fullWidth />
                  <TextField label="German message" value={maintenanceEmail.messageDe} onChange={(event) => setMaintenanceEmail((current) => ({ ...current, messageDe: event.target.value }))} multiline minRows={4} fullWidth />
                  <TextField label="Arabic message" value={maintenanceEmail.messageAr} onChange={(event) => setMaintenanceEmail((current) => ({ ...current, messageAr: event.target.value }))} multiline minRows={4} fullWidth inputProps={{ dir: "rtl" }} />
                  <Button variant="outlined" onClick={() => setMaintenancePreviewOpen(true)} disabled={!maintenanceEmail.subject.trim() || !maintenanceEmail.messageEn.trim() || !maintenanceEmail.messageDe.trim() || !maintenanceEmail.messageAr.trim()}>Preview maintenance email</Button>
                </Stack>
              </Box>
              <Box sx={{ pt: 3, mt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" fontWeight={700}>Feature Release Email</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>Create a three-language feature announcement, add an image and link, then edit and preview it before sending.</Typography>
                <Stack spacing={2}>
                  <TextField label="Email subject" value={featureReleaseEmail.subject} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, subject: event.target.value }))} fullWidth />
                  <TextField label="English message" value={featureReleaseEmail.messageEn} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, messageEn: event.target.value }))} multiline minRows={4} fullWidth inputProps={{ dir: "ltr" }} />
                  <TextField label="German message" value={featureReleaseEmail.messageDe} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, messageDe: event.target.value }))} multiline minRows={4} fullWidth inputProps={{ dir: "ltr" }} />
                  <TextField label="Arabic message" value={featureReleaseEmail.messageAr} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, messageAr: event.target.value }))} multiline minRows={4} fullWidth inputProps={{ dir: "rtl" }} />
                  <TextField label="Link URL" type="url" value={featureReleaseEmail.linkUrl} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, linkUrl: event.target.value }))} helperText="Use a complete https:// link." fullWidth />
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField label="English button text" value={featureReleaseEmail.linkTextEn} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, linkTextEn: event.target.value }))} fullWidth />
                    <TextField label="German button text" value={featureReleaseEmail.linkTextDe} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, linkTextDe: event.target.value }))} fullWidth />
                    <TextField label="Arabic button text" value={featureReleaseEmail.linkTextAr} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, linkTextAr: event.target.value }))} fullWidth inputProps={{ dir: "rtl" }} />
                  </Stack>
                  <FormControl fullWidth>
                    <InputLabel>Image position</InputLabel>
                    <Select label="Image position" value={featureReleaseEmail.imagePosition} onChange={(event) => setFeatureReleaseEmail((current) => ({ ...current, imagePosition: event.target.value }))}>
                      <MenuItem value="TOP">Above all messages</MenuItem>
                      <MenuItem value="BETWEEN">Between English and German</MenuItem>
                      <MenuItem value="BOTTOM">Below all messages</MenuItem>
                    </Select>
                  </FormControl>
                  <Button component="label" variant="outlined">
                    {featureReleaseImage ? `Change image: ${featureReleaseImage.name}` : "Attach image (optional, max. 5 MB)"}
                    <input hidden type="file" accept="image/*" onChange={selectFeatureReleaseImage} />
                  </Button>
                  {featureReleaseImage && <Button color="error" onClick={() => { URL.revokeObjectURL(featureReleaseImagePreview); setFeatureReleaseImage(null); setFeatureReleaseImagePreview(""); }}>Remove image</Button>}
                  <Button variant="contained" onClick={() => setFeatureReleasePreviewOpen(true)} disabled={!featureReleaseEmail.subject.trim() || !featureReleaseEmail.messageEn.trim() || !featureReleaseEmail.messageDe.trim() || !featureReleaseEmail.messageAr.trim()}>Preview feature release email</Button>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        )}

        {tab === 2 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={4}>
              <Box sx={{ maxWidth: 560 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Placement test attempts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set how many times a student may start the placement test for each target level.
                  Existing attempts are counted against the new limit immediately.
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                {loadingAttempts ? (
                  <CircularProgress size={28} sx={{ mt: 2 }} />
                ) : (
                  <TextField
                    label="Maximum attempts per level"
                    type="number"
                    value={maxAttempts}
                    onChange={(event) => setMaxAttempts(event.target.value)}
                    inputProps={{ min: 1, max: 100, step: 1 }}
                    helperText="Allowed range: 1-100 attempts"
                    disabled={savingAttempts}
                    fullWidth
                    sx={{ mt: 2 }}
                  />
                )}
                <Button
                  variant="contained"
                  onClick={savePlacementSettings}
                  disabled={savingAttempts || loadingAttempts}
                  sx={{ mt: 2 }}
                >
                  {savingAttempts ? "Saving..." : "Save attempt limit"}
                </Button>
              </Box>

              <Box>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Placement Test Recovery
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trace submissions, recover saved answers, retry result emails, and release failed attempts.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => loadPlacementSessions(placementPage)} disabled={loadingSessions}>
                    Refresh
                  </Button>
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }, gap: 1.25, mt: 2 }}>
                  {Object.entries(RECOVERY_CATEGORIES).map(([key, item]) => (
                    <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                      <Chip size="small" label={item.label} color={item.color} sx={{ mb: 0.75, fontWeight: 700 }} />
                      <Typography variant="caption" display="block" color="text.secondary">{item.meaning}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mt: 2 }} alignItems={{ lg: "center" }}>
                  <TextField size="small" label="Name, email or session ID" value={placementFilters.search} onChange={(event) => setPlacementFilters((current) => ({ ...current, search: event.target.value }))} sx={{ minWidth: 240 }} />
                  <FormControl size="small" sx={{ minWidth: 190 }}><InputLabel>Recovery status</InputLabel><Select label="Recovery status" value={placementFilters.category} onChange={(event) => setPlacementFilters((current) => ({ ...current, category: event.target.value }))}><MenuItem value="">All statuses</MenuItem>{Object.entries(RECOVERY_CATEGORIES).map(([key, item]) => <MenuItem key={key} value={key}>{item.label}</MenuItem>)}</Select></FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Account</InputLabel><Select label="Account" value={placementFilters.accountType} onChange={(event) => setPlacementFilters((current) => ({ ...current, accountType: event.target.value }))}><MenuItem value="">All accounts</MenuItem><MenuItem value="GUEST">Guest</MenuItem><MenuItem value="REGISTERED">Registered</MenuItem></Select></FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}><InputLabel>Email status</InputLabel><Select label="Email status" value={placementFilters.emailStatus} onChange={(event) => setPlacementFilters((current) => ({ ...current, emailStatus: event.target.value }))}><MenuItem value="">All email states</MenuItem>{["NOT_ATTEMPTED","UNKNOWN","SENT","FAILED"].map((value) => <MenuItem key={value} value={value}>{value.replaceAll("_", " ")}</MenuItem>)}</Select></FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Level</InputLabel><Select label="Level" value={placementFilters.targetLevel} onChange={(event) => setPlacementFilters((current) => ({ ...current, targetLevel: event.target.value }))}><MenuItem value="">All</MenuItem>{["A2","B1","B2","C1"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</Select></FormControl>
                  <Button variant="contained" onClick={() => loadPlacementSessions(0)} disabled={loadingSessions}>Search</Button>
                  <Button variant="text" onClick={() => { const cleared = { search: "", category: "", accountType: "", emailStatus: "", targetLevel: "" }; setPlacementFilters(cleared); loadPlacementSessions(0, cleared); }} disabled={loadingSessions}>Clear</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>{placementTotalElements} matching sessions</Typography>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" disabled={!selectedRows.some((r) => r.recoveryCategory === "READY_FOR_RE_EVALUATION") || sessionAction !== null}
                    onClick={() => runRecoveryAction("re-evaluate", selectedRows.filter((r) => r.recoveryCategory === "READY_FOR_RE_EVALUATION"))}>
                    Re-evaluate selected
                  </Button>
                  <Button variant="outlined" disabled={!selectedRows.some((r) => r.evaluationId) || sessionAction !== null}
                    onClick={() => runRecoveryAction("result-email", selectedRows.filter((r) => r.evaluationId))}>
                    Send result email
                  </Button>
                  <Button variant="outlined" color="warning" disabled={!selectedRows.some((r) => ["IN_PROGRESS","ABANDONED","SUBMISSION_FAILED_RETAKE_REQUIRED","TIME_EXPIRED_RETAKE_REQUIRED"].includes(r.recoveryCategory)) || sessionAction !== null}
                    onClick={() => runRecoveryAction("allow-retake-and-notify", selectedRows.filter((r) => ["IN_PROGRESS","ABANDONED","SUBMISSION_FAILED_RETAKE_REQUIRED","TIME_EXPIRED_RETAKE_REQUIRED"].includes(r.recoveryCategory)))}>
                    Allow retake &amp; send email
                  </Button>
                  <Button variant="outlined" disabled={!selectedRows.some((r) => r.recoveryCategory === "IN_PROGRESS") || sessionAction !== null}
                    onClick={() => runRecoveryAction("continue-reminder", selectedRows.filter((r) => r.recoveryCategory === "IN_PROGRESS"))}>Send continue reminder</Button>
                  <Button variant="outlined" color="warning" disabled={!selectedRows.some((r) => ["EVALUATION_CONFLICT","EXISTING_RESULT_UNFINISHED_SESSION"].includes(r.recoveryCategory)) || sessionAction !== null}
                    onClick={() => {
                      const rows = selectedRows.filter((r) => ["EVALUATION_CONFLICT","EXISTING_RESULT_UNFINISHED_SESSION"].includes(r.recoveryCategory));
                      if (window.confirm(`Preserve the existing evaluation and close ${rows.length} selected session${rows.length === 1 ? "" : "s"}? Unfinished attempts will be released.`)) {
                        runRecoveryAction("mark-superseded", rows);
                      }
                    }}>Keep existing result &amp; close session</Button>
                  <Button variant="outlined" color="error" disabled={!selectedRows.length || sessionAction !== null}
                    onClick={() => setApologyPreviewOpen(true)}>
                    Preview apology email
                  </Button>
                </Stack>

                {loadingSessions ? (
                  <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress /></Box>
                ) : (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox checked={placementSessions.length > 0 && selectedSessions.length === placementSessions.length}
                              indeterminate={selectedSessions.length > 0 && selectedSessions.length < placementSessions.length}
                              onChange={(event) => setSelectedSessions(event.target.checked ? placementSessions.map((row) => row.sessionId) : [])} />
                          </TableCell>
                          <TableCell>Student</TableCell>
                          <TableCell>Account</TableCell>
                          <TableCell>Session / target</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Started / submitted</TableCell>
                          <TableCell>Scores</TableCell>
                          <TableCell>Evaluation</TableCell>
                          <TableCell>Error / recovery</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {placementSessions.length === 0 && (
                          <TableRow><TableCell colSpan={11} align="center">No placement sessions found.</TableCell></TableRow>
                        )}
                        {placementSessions.map((row) => (
                          <TableRow key={row.sessionId}>
                            <TableCell padding="checkbox"><Checkbox checked={selectedSessions.includes(row.sessionId)} onChange={() => toggleSession(row.sessionId)} /></TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{row.studentName || `Student ${row.studentId}`}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.email || "-"}</Typography>
                            </TableCell>
                            <TableCell><Chip size="small" label={row.accountType || "UNKNOWN"} color={row.accountType === "GUEST" ? "warning" : "default"} /><Typography variant="caption" display="block" color="text.secondary">{row.accountState || "-"}<br/>{row.emailVerified ? "Email verified" : "Email unverified"}{row.emailSyntaxValid === false ? " · invalid syntax" : ""}</Typography></TableCell>
                            <TableCell><strong>#{row.sessionId}</strong><br/><small>{row.targetLevel || "-"} · attempt {row.attemptsForLevel}</small></TableCell>
                            <TableCell><Chip size="small" color={RECOVERY_CATEGORIES[row.recoveryCategory]?.color || "default"} label={RECOVERY_CATEGORIES[row.recoveryCategory]?.label || row.recoveryCategory || "Unknown"} /></TableCell>
                            <TableCell><small>Start: {formatSessionDate(row.startedAt)}<br/>Submit: {formatSessionDate(row.submittedAt)}</small></TableCell>
                            <TableCell><small>Raw: {row.autoScoredScore ?? "-"}<br/>Total: {row.totalScore ?? "-"}<br/>Fully scored: {row.fullyScored == null ? "-" : row.fullyScored ? "Yes" : "No"}</small></TableCell>
                            <TableCell><small>{row.evaluationStatus || "Missing"}<br/>Final: {row.finalLevel || "-"}<br/>Normalized: {row.normalizedScore ?? "-"}{row.existingEvaluationId && <><br/><strong>Other evaluation #{row.existingEvaluationId}</strong><br/>Session #{row.existingEvaluationSessionId} · {row.existingEvaluationFinalLevel || "-"}<br/>{row.existingEvaluationNewer ? "Existing evaluation is newer" : "Selected answer may be newer"}</>}</small></TableCell>
                            <TableCell sx={{ maxWidth: 230 }}><small>{row.errorCode || "No recorded error"}<br/>{row.errorStage || "-"}<br/>{row.recoveryStatus || "NONE"}</small></TableCell>
                            <TableCell><Chip size="small" color={row.resultEmailStatus === "SENT" ? "success" : row.resultEmailStatus === "FAILED" ? "error" : "default"} label={row.resultEmailStatus || "UNKNOWN"} /></TableCell>
                            <TableCell align="right">
                              <Stack direction="column" spacing={0.5} alignItems="flex-end">
                                <Button size="small" onClick={() => viewTechnicalDetails(row)} disabled={sessionAction !== null}>Technical details</Button>
                                {row.active && (
                                  <Button
                                    size="small"
                                    color="warning"
                                    onClick={() => closePlacementSession(row.sessionId)}
                                    disabled={sessionAction !== null}
                                  >
                                    {sessionAction === `close-${row.sessionId}` ? "Closing..." : "Close"}
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => resetPlacementAttempts(row)}
                                  disabled={!row.targetLevel || sessionAction !== null}
                                >
                                  {sessionAction === `reset-${row.studentId}-${row.targetLevel}` ? "Resetting..." : "Reset all attempts"}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {placementTotalPages > 1 && <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><Pagination count={placementTotalPages} page={placementPage + 1} onChange={(event, value) => loadPlacementSessions(value - 1)} disabled={loadingSessions} /></Box>}
              </Box>

              <Box>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Placement guest accounts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Correct a guest email, verify email delivery, or permanently remove an unconverted guest and all of their placement data. Registered accounts are protected and never appear here.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      label="Search guests"
                      value={guestSearch}
                      onChange={(event) => setGuestSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") loadPlacementGuests(guestSearch);
                      }}
                    />
                    <Button variant="outlined" onClick={() => loadPlacementGuests(guestSearch)} disabled={loadingGuests}>
                      Search
                    </Button>
                  </Stack>
                </Stack>

                {loadingGuests ? (
                  <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress /></Box>
                ) : (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Guest</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell sx={{ minWidth: 280 }}>Email</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {placementGuests.length === 0 && (
                          <TableRow><TableCell colSpan={6} align="center">No placement guest accounts found.</TableCell></TableRow>
                        )}
                        {placementGuests.map((guest) => (
                          <TableRow key={guest.userId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {[guest.firstName, guest.lastName].filter(Boolean).join(" ") || `Guest ${guest.userId}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Student ID: {guest.studentId || "-"}</Typography>
                            </TableCell>
                            <TableCell>{guest.phoneNumber || "-"}</TableCell>
                            <TableCell>{guest.accountState || "-"}</TableCell>
                            <TableCell>{formatSessionDate(guest.guestCreatedAt)}</TableCell>
                            <TableCell>
                              <TextField
                                type="email"
                                size="small"
                                fullWidth
                                value={guestEmailDrafts[guest.userId] ?? ""}
                                onChange={(event) => setGuestEmailDrafts((current) => ({
                                  ...current,
                                  [guest.userId]: event.target.value,
                                }))}
                                disabled={guestAction !== null}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                <Button
                                  size="small"
                                  onClick={() => updateGuestEmail(guest)}
                                  disabled={guestAction !== null || (guestEmailDrafts[guest.userId] || "").trim() === guest.email}
                                >
                                  {guestAction === `email-${guest.userId}` ? "Saving..." : "Save email"}
                                </Button>
                                <Button size="small" onClick={() => sendGuestTestEmail(guest)} disabled={guestAction !== null}>
                                  {guestAction === `test-${guest.userId}` ? "Sending..." : "Test email"}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => deletePlacementGuest(guest)}
                                  disabled={guestAction !== null}
                                >
                                  {guestAction === `delete-${guest.userId}` ? "Deleting..." : "Delete guest"}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {tab === 3 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Social media links</Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave a link empty to show “Coming soon” when its footer icon is clicked. Links must start with http:// or https://.
                </Typography>
              </Box>
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}
              {loadingSocialLinks ? <CircularProgress size={28} /> : SOCIAL_PLATFORMS.map((platform) => (
                <TextField
                  key={platform}
                  fullWidth
                  label={`${platform.charAt(0).toUpperCase()}${platform.slice(1)} URL`}
                  placeholder={platform === "telegram" ? "https://t.me/..." : `https://${platform}.com/...`}
                  value={socialLinks[platform] || ""}
                  onChange={(event) => setSocialLinks((current) => ({ ...current, [platform]: event.target.value }))}
                  inputProps={{ dir: "ltr" }}
                  disabled={savingSocialLinks}
                />
              ))}
              <Button variant="contained" onClick={saveSocialLinks} disabled={loadingSocialLinks || savingSocialLinks} sx={{ alignSelf: "flex-start" }}>
                {savingSocialLinks ? "Saving..." : "Save social links"}
              </Button>
            </Stack>
          </Paper>
        )}

        {tab === 4 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
              <Box><Typography variant="h6" fontWeight={700}>Teacher Manager permissions</Typography><Typography variant="body2" color="text.secondary">These settings control both visibility and backend authorization. Destructive actions are disabled by default.</Typography></Box>
              {error && <Alert severity="error">{error}</Alert>}{success && <Alert severity="success">{success}</Alert>}
              {loadingManagerPermissions ? <CircularProgress size={28}/> : Object.entries(MANAGER_PERMISSION_LABELS).map(([key, label]) => (
                <FormControlLabel key={key} control={<Switch checked={Boolean(managerPermissions[key])} onChange={(event) => setManagerPermissions((current) => ({ ...current, [key]: event.target.checked }))} />} label={label} />
              ))}
              <Alert severity="warning">Permanent deletion is still blocked by the backend when linked enrollments or related records make deletion unsafe. Archive should be preferred.</Alert>
              <Button variant="contained" onClick={saveManagerPermissions} disabled={loadingManagerPermissions || savingManagerPermissions} sx={{ alignSelf: "flex-start" }}>{savingManagerPermissions ? "Saving..." : "Save permissions"}</Button>
            </Stack>
          </Paper>
        )}

        {tab === 5 && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
              <Box><Typography variant="h6" fontWeight={700}>Payment method visibility</Typography><Typography variant="body2" color="text.secondary">Controls what students can see and what the backend accepts. Existing payment receipts can still be uploaded after a method is hidden.</Typography></Box>
              {error && <Alert severity="error">{error}</Alert>}{success && <Alert severity="success">{success}</Alert>}
              {loadingPaymentMethods ? <CircularProgress size={28}/> : (
                <>
                  <FormControlLabel control={<Switch checked={Boolean(paymentMethods.onlinePaymentsEnabled)} onChange={(event) => setPaymentMethods((current) => ({ ...current, onlinePaymentsEnabled: event.target.checked }))} />} label="Online payments (Stripe and PayPal)" />
                  <FormControlLabel control={<Switch checked={Boolean(paymentMethods.generalPaymentsEnabled)} onChange={(event) => setPaymentMethods((current) => ({ ...current, generalPaymentsEnabled: event.target.checked }))} />} label="General international payments (SEPA and SWIFT)" />
                  <FormControlLabel control={<Switch checked={Boolean(paymentMethods.egyptianBankTransferEnabled)} onChange={(event) => setPaymentMethods((current) => ({ ...current, egyptianBankTransferEnabled: event.target.checked }))} />} label="Egyptian bank transfer (Banque Misr)" />
                  <TextField label="Egyptian bank IBAN" value={paymentMethods.egyptianBankIban || ""} onChange={(event) => setPaymentMethods((current) => ({ ...current, egyptianBankIban: event.target.value.toUpperCase() }))} disabled={!paymentMethods.egyptianBankTransferEnabled} helperText="Required when Egyptian bank transfer is enabled." inputProps={{ dir: "ltr" }} fullWidth />
                  <FormControlLabel control={<Switch checked={Boolean(paymentMethods.instaPayEnabled)} onChange={(event) => setPaymentMethods((current) => ({ ...current, instaPayEnabled: event.target.checked }))} />} label="InstaPay" />
                </>
              )}
              <Alert severity="info">Egyptian options remain limited to students identified as being in Egypt. Each Egyptian method can be enabled independently.</Alert>
              <Button variant="contained" onClick={savePaymentMethods} disabled={loadingPaymentMethods || savingPaymentMethods} sx={{ alignSelf: "flex-start" }}>{savingPaymentMethods ? "Saving..." : "Save payment methods"}</Button>
            </Stack>
          </Paper>
        )}

        {tab === 6 && <PlacementTestAnalytics />}

        {tab === 7 && <CourseInterestEmailGenerator />}

        <Dialog open={maintenancePreviewOpen} onClose={() => !sendingMaintenance && setMaintenancePreviewOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Preview maintenance email</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" fontWeight={800} mb={2}>{maintenanceEmail.subject}</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Typography variant="overline">English</Typography><Typography sx={{ whiteSpace: "pre-line" }}>{maintenanceEmail.messageEn}</Typography></Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Typography variant="overline">Deutsch</Typography><Typography sx={{ whiteSpace: "pre-line" }}>{maintenanceEmail.messageDe}</Typography></Paper>
            <Paper variant="outlined" sx={{ p: 2, textAlign: "right" }} dir="rtl"><Typography variant="overline">العربية</Typography><Typography sx={{ whiteSpace: "pre-line" }}>{maintenanceEmail.messageAr}</Typography></Paper>
            <Alert severity="warning" sx={{ mt: 2 }}>Confirming will send this email to every unique email address stored for a user.</Alert>
          </DialogContent>
          <DialogActions><Button onClick={() => setMaintenancePreviewOpen(false)} disabled={sendingMaintenance}>Back to edit</Button><Button variant="contained" color="warning" onClick={sendMaintenanceEmail} disabled={sendingMaintenance}>{sendingMaintenance ? "Sending..." : "Confirm and send to all users"}</Button></DialogActions>
        </Dialog>

        <Dialog open={featureReleasePreviewOpen} onClose={() => !sendingFeatureRelease && setFeatureReleasePreviewOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Preview feature release email</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" fontWeight={800} mb={2}>{featureReleaseEmail.subject}</Typography>
            {featureReleaseImagePreview && featureReleaseEmail.imagePosition === "TOP" && <Box component="img" src={featureReleaseImagePreview} alt="Feature release" sx={{ width: "100%", maxHeight: 420, objectFit: "cover", borderRadius: 2, mb: 2 }} />}
            {[
              { language: "English", message: featureReleaseEmail.messageEn, linkText: featureReleaseEmail.linkTextEn, rtl: false },
              { language: "Deutsch", message: featureReleaseEmail.messageDe, linkText: featureReleaseEmail.linkTextDe, rtl: false },
              { language: "العربية", message: featureReleaseEmail.messageAr, linkText: featureReleaseEmail.linkTextAr, rtl: true },
            ].map((section, index) => (
              <React.Fragment key={section.language}>
                {featureReleaseImagePreview && featureReleaseEmail.imagePosition === "BETWEEN" && index === 1 && <Box component="img" src={featureReleaseImagePreview} alt="Feature release" sx={{ width: "100%", maxHeight: 420, objectFit: "cover", borderRadius: 2, mb: 2 }} />}
                <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: section.rtl ? "right" : "left" }} dir={section.rtl ? "rtl" : "ltr"}>
                  <Typography variant="overline">{section.language}</Typography>
                  <Typography sx={{ whiteSpace: "pre-line" }}>{section.message}</Typography>
                  {featureReleaseEmail.linkUrl && <Button component="a" href={featureReleaseEmail.linkUrl} target="_blank" rel="noreferrer" variant="contained" sx={{ mt: 2 }}>{section.linkText || "Learn more"}</Button>}
                </Paper>
              </React.Fragment>
            ))}
            {featureReleaseImagePreview && featureReleaseEmail.imagePosition === "BOTTOM" && <Box component="img" src={featureReleaseImagePreview} alt="Feature release" sx={{ width: "100%", maxHeight: 420, objectFit: "cover", borderRadius: 2, mb: 2 }} />}
            <Alert severity="warning">Confirming will send this email to every unique email address stored for a user.</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFeatureReleasePreviewOpen(false)} disabled={sendingFeatureRelease}>Back to edit</Button>
            <Button variant="contained" color="warning" onClick={sendFeatureReleaseEmail} disabled={sendingFeatureRelease}>{sendingFeatureRelease ? "Sending..." : "Confirm and send to all users"}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(technicalDetails)} onClose={() => setTechnicalDetails(null)} fullWidth maxWidth="md">
          <DialogTitle>Placement technical details · session #{technicalDetails?.row?.sessionId}</DialogTitle>
          <DialogContent dividers>
            {technicalDetails && (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2"><strong>Student:</strong> {technicalDetails.row.studentName || technicalDetails.row.studentId} ({technicalDetails.row.email || "no email"})</Typography>
                  <Typography variant="body2"><strong>Account:</strong> {technicalDetails.row.accountType || "UNKNOWN"} · {technicalDetails.row.accountState || "-"} · {technicalDetails.row.emailVerified ? "verified email" : "unverified email"} · {technicalDetails.row.emailSyntaxValid === false ? "invalid syntax" : "syntax valid"}</Typography>
                  <Typography variant="body2"><strong>Target / final:</strong> {technicalDetails.row.targetLevel || "-"} / {technicalDetails.row.finalLevel || "-"}</Typography>
                  <Typography variant="body2"><strong>Answer / evaluation:</strong> {technicalDetails.row.answerId || "missing"} / {technicalDetails.row.evaluationId || "missing"}</Typography>
                  <Typography variant="body2"><strong>Latest error:</strong> {technicalDetails.row.errorCode || "No recorded error"} · {technicalDetails.row.errorStage || "-"}</Typography>
                  {technicalDetails.row.errorDetails && <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{technicalDetails.row.errorDetails}</Typography>}
                  {technicalDetails.row.existingEvaluationId && (
                    <Alert severity={technicalDetails.row.recoveryCategory === "RETAKE_RESOLVED" ? "success" : "warning"} sx={{ mt: 2 }}>
                      Evaluation #{technicalDetails.row.existingEvaluationId}, linked to session #{technicalDetails.row.existingEvaluationSessionId}, contains the existing {technicalDetails.row.existingEvaluationFinalLevel || "-"} result finalized {formatSessionDate(technicalDetails.row.existingEvaluationFinalizedAt)}.{" "}
                      {technicalDetails.row.recoveryCategory === "RETAKE_RESOLVED"
                        ? "This unfinished attempt has already been released for a clean retake. No additional recovery action is required."
                        : !technicalDetails.row.answerId || !technicalDetails.row.submitted
                          ? "This session has no submitted answer and cannot be re-evaluated. Keep the existing result and close this unfinished session, or explicitly release it for a retake."
                          : technicalDetails.row.existingEvaluationNewer
                            ? "The existing evaluation is newer and should normally be preserved."
                            : "This session has a submitted, scored answer that may be newer. Compare timestamps and scores before deciding which result to preserve."}
                    </Alert>
                  )}
                </Paper>
                <Typography variant="subtitle1" fontWeight={700}>Recovery and delivery history</Typography>
                {technicalDetails.history.length === 0 ? <Alert severity="info">No tracking events exist. Older production attempts predate this feature.</Alert> : technicalDetails.history.map((event) => (
                  <Paper key={event.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={event.eventType} /><Chip size="small" color={event.status === "FAILED" ? "error" : "success"} label={event.status} /><Typography variant="caption">{formatSessionDate(event.occurredAt)}</Typography></Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>{event.stage || "-"} · {event.errorCode || "No error code"}</Typography>
                    <Typography variant="caption" color="text.secondary">{event.details || "-"}</Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setTechnicalDetails(null)}>Close</Button></DialogActions>
        </Dialog>

        <Dialog open={apologyPreviewOpen} onClose={() => setApologyPreviewOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit and preview apology email</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>This will send a real email to {selectedRows.length} selected student{selectedRows.length === 1 ? "" : "s"}. Use “Allow retake” first for attempts that must be released.</Alert>
            <Alert severity="info" sx={{ mb: 2 }}>Use <strong>{"{studentName}"}</strong> anywhere in the text. It will be replaced separately with each selected student’s name.</Alert>
            <Stack spacing={2}>
              <TextField label="Email subject" fullWidth value={apologyEmail.subject} onChange={(event) => setApologyEmail((current) => ({ ...current, subject: event.target.value }))} />
              <TextField label="English message" fullWidth required multiline minRows={7} value={apologyEmail.messageEn} onChange={(event) => setApologyEmail((current) => ({ ...current, messageEn: event.target.value }))} inputProps={{ dir: "ltr" }} />
              <TextField label="German message" fullWidth required multiline minRows={7} value={apologyEmail.messageDe} onChange={(event) => setApologyEmail((current) => ({ ...current, messageDe: event.target.value }))} inputProps={{ dir: "ltr" }} />
              <TextField label="Arabic message" fullWidth required multiline minRows={7} value={apologyEmail.messageAr} onChange={(event) => setApologyEmail((current) => ({ ...current, messageAr: event.target.value }))} inputProps={{ dir: "rtl" }} />
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                <Typography variant="subtitle2" gutterBottom>Personalized preview for the first selected student</Typography>
                {[apologyEmail.messageEn, apologyEmail.messageDe, apologyEmail.messageAr].map((message, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Box component="hr" sx={{ border: 0, borderTop: "1px solid #ddd", my: 2 }} />}
                    <Typography variant="body2" dir={index === 2 ? "rtl" : "ltr"} sx={{ whiteSpace: "pre-line" }}>
                      {message.replaceAll("{studentName}", selectedRows[0]?.studentName || "Student Name")}
                    </Typography>
                  </React.Fragment>
                ))}
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApologyEmail(DEFAULT_APOLOGY_EMAIL)}>Reset text</Button>
            <Button onClick={() => setApologyPreviewOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" disabled={!apologyEmail.messageEn.trim() || !apologyEmail.messageDe.trim() || !apologyEmail.messageAr.trim()}
              onClick={async () => { setApologyPreviewOpen(false); await runRecoveryAction("apology-email", selectedRows, apologyEmail); }}>Send to selected</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AdminSettingsPage;
